// @ts-strict-ignore
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Columns } from "@fullscript/aviary-web";
import { Trans, useTranslation } from "react-i18next";

import { FloatingLabelInput, PhoneInput } from "@aviary";
import { removePlusSign } from "@aviary/components/PhoneInput/PhoneInput";
import { AddressPulldownView } from "@shared/components/AddressPulldown/AddressPulldownView/AddressPulldownView";
import { useAddressAutocomplete } from "@shared/hooks/useAddressAutocomplete/useAddressAutocomplete";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import type {
  AddressInformationData,
  AddressInformationErrors,
} from "@shared/types/fields/addressInformationTypes";

import { IconWrapper } from "../../IconWrapper/IconWrapper";
import { StateSelect } from "../../StateSelect/StateSelect";
import { ValidationSummary } from "../../ValidationSummary/ValidationSummary";

import { useStatesInfoQuery } from "./StatesInfo.query";
import { placesAutocompleteBoilerplate } from "./placesAutocompleteBoilerplate";

import * as styles from "./AddressInfoFields.styles";

interface Props {
  addressInformationData: AddressInformationData;
  addressInformationErrors: AddressInformationErrors | null;
  handleSetAddressInformation: (addressInfo: AddressInformationData) => void;
  setIsDirty?: (boolean) => void;
  isFullWidth?: boolean;
  showStreetAddress2?: boolean;
  showPhoneNumber?: boolean;
  useSingleColumn?: boolean;
  isMarginLess?: boolean;
  idPrepend?: string; // New prop for prepending IDs
}

const AddressInfoFields = ({
  addressInformationErrors,
  addressInformationData,
  handleSetAddressInformation,
  setIsDirty,
  isFullWidth = false,
  showStreetAddress2 = false,
  showPhoneNumber = true,
  useSingleColumn = false,
  isMarginLess = false,
  idPrepend = "", // Default to empty string if not provided
}: Props) => {
  const { t } = useTranslation();
  const { isUS, country } = useSharedGlobalConfig();

  const { data } = useStatesInfoQuery();
  const updateAddressInfo = (id, value) => {
    handleSetAddressInformation({ ...addressInformationData, [id]: value });
  };

  const { onSuggestionSelect, handleBlur } = placesAutocompleteBoilerplate(
    addressInformationData,
    handleSetAddressInformation,
    data
  );

  const {
    onBlur,
    onChange,
    onKeyDown,
    value: addressValue,
    getSuggestionItemProps,
    suggestions,
    loading,
  } = useAddressAutocomplete({
    defaultValue: addressInformationData?.streetAddress ?? "",
    onChange: updatedValue =>
      onAddressChange({ target: { id: `${idPrepend}streetAddress`, value: updatedValue } }),
    onSelect: onSuggestionSelect,
    requestOptions: {
      componentRestrictions: { country: country === "United States" ? "us" : "ca" },
    },
    shouldFetchSuggestions: addressInformationData?.streetAddress?.length > 3,
  });

  const onAddressChange = e => {
    const { value, id } = e.target;
    updateAddressInfo(id.replace(idPrepend, ""), value);
    if (setIsDirty) {
      setIsDirty(true);
    }
  };

  const onPhoneNumberChange = value => {
    updateAddressInfo("phone", removePlusSign(value));
  };

  const {
    streetAddress: streetAddressErrors,
    streetAddress2: streetAddress2Errors,
    city: cityErrors,
    region: regionErrors,
    postalCode: postalCodeErrors,
    phone: phoneErrors,
  } = addressInformationErrors?.fields ?? {};

  const topLevelErrors = addressInformationErrors?.message && [addressInformationErrors.message];

  const renderPhoneTooltip = () => {
    return (
      <Trans i18nKey={l.sharedSettings.tax.addressInfo.phoneTooltip}>
        We're here to help. Providing this information helps our team give our users a{" "}
        <strong>faster</strong> support experience
      </Trans>
    );
  };

  const regionLabel = isUS
    ? t(l.sharedSettings.tax.addressInfo.state)
    : t(l.sharedSettings.tax.addressInfo.province);

  const postalCodeLabel = isUS
    ? t(l.sharedSettings.tax.addressInfo.zipcode)
    : t(l.sharedSettings.tax.addressInfo.postalCode);

  return (
    <Columns isMarginless={isMarginLess}>
      <Columns.Column columnWidth={useSingleColumn && !isFullWidth ? 6 : 12} tabletWidth={12}>
        <AddressPulldownView
          data-testid={`${idPrepend}street-address-input`}
          suggestions={suggestions}
          onBlur={handleBlur(onBlur, suggestions)}
          onChange={onChange}
          onKeyDown={onKeyDown}
          getSuggestionItemProps={getSuggestionItemProps}
          value={addressValue ?? ""}
          id={`${idPrepend}streetAddress`}
          label={t(l.sharedSettings.tax.addressInfo.streetAddress)}
          name={t(l.sharedSettings.tax.addressInfo.streetAddress)}
          errors={streetAddressErrors}
          loading={loading}
          maxLength={35}
          data-e2e={`${idPrepend}street-address`}
        />
      </Columns.Column>
      {!!showStreetAddress2 && (
        <Columns.Column columnWidth={isFullWidth ? 12 : 6} tabletWidth={12}>
          <FloatingLabelInput
            type="text"
            id={`${idPrepend}streetAddress2`}
            label={t(l.sharedSettings.tax.addressInfo.streetAddress2)}
            name={t(l.sharedSettings.tax.addressInfo.streetAddress2)}
            value={addressInformationData?.streetAddress2 ?? ""}
            handleChange={onAddressChange}
            errors={streetAddress2Errors}
            maxLength={35}
            data-e2e={`${idPrepend}street-address-2`}
          />
        </Columns.Column>
      )}
      <Columns.Column columnWidth={isFullWidth ? 12 : 6} tabletWidth={12}>
        <FloatingLabelInput
          data-testid={`${idPrepend}city-input`}
          type="text"
          id={`${idPrepend}city`}
          name={t(l.sharedSettings.tax.addressInfo.city)}
          label={t(l.sharedSettings.tax.addressInfo.city)}
          value={addressInformationData?.city ?? ""}
          handleChange={onAddressChange}
          required
          errors={cityErrors}
          maxLength={30}
          data-e2e={`${idPrepend}city`}
        />
      </Columns.Column>
      <Columns.Column columnWidth={isFullWidth ? 12 : 6} tabletWidth={12}>
        <StateSelect
          id={`${idPrepend}region`}
          onChange={onAddressChange}
          value={addressInformationData?.region ?? ""}
          label={regionLabel}
          name={regionLabel}
          placeholder={t(l.sharedCommon.Select)}
          isRequired
          errors={regionErrors}
          data-e2e={`${idPrepend}region`}
        />
      </Columns.Column>
      <Columns.Column columnWidth={isFullWidth ? 12 : 6} tabletWidth={12}>
        <FloatingLabelInput
          type="text"
          id={`${idPrepend}postalCode`}
          label={postalCodeLabel}
          name={postalCodeLabel}
          value={addressInformationData?.postalCode ?? ""}
          handleChange={onAddressChange}
          required
          errors={postalCodeErrors}
          data-e2e={`${idPrepend}postal-code`}
        />
      </Columns.Column>
      {!!showPhoneNumber && (
        <Columns.Column columnWidth={isFullWidth ? 12 : 6} tabletWidth={12}>
          <IconWrapper icon={faInfoCircle} tooltipMessage={renderPhoneTooltip()}>
            <div css={styles.phoneInput}>
              <PhoneInput
                id={`${idPrepend}phone`}
                label={t(l.sharedSettings.tax.addressInfo.phoneNumber)}
                name={t(l.sharedSettings.tax.addressInfo.phoneNumber)}
                aria-label={t(l.sharedSettings.tax.addressInfo.phoneNumber)}
                value={addressInformationData?.phone ?? ""}
                onChange={onPhoneNumberChange}
                required
                errors={phoneErrors}
                preferredCountryCode={isUS ? "US" : "CA"}
                data-e2e={`${idPrepend}phone`}
              />
            </div>
          </IconWrapper>
        </Columns.Column>
      )}
      <ValidationSummary errors={topLevelErrors} css={styles.validationSummary} />
    </Columns>
  );
};

export { AddressInfoFields, type Props as AddressInfoFieldsProps };
